import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { hubClient } from '../../services/hubClient';
import { errorText } from '../../lib/assistantView';
import type { BotSystem } from '../../lib/botNav';
import { fmtTime } from '../../lib/jobView';
import { latestGate } from '../../lib/answerReviewView';
import {
  accuracyText,
  correctionError,
  needsConfirm,
  ruleSource,
  type Correction,
  type CorrectionChange,
  type CorrectionEvaluation,
  type TextEvalCase,
} from '../../lib/latinView';

const saveError = (e: unknown) => correctionError(e instanceof Error ? e.message : String(e));
type RulePatch = { cyrillic?: string; enabled?: boolean; suggestGlobal?: boolean; confirm?: boolean };
const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Quality › Latin text › Corrections (Mungun/infra text-conversion review, P2): this bot's rules — edit, switch off,
 * history — and, read-only, the all-bots rules that apply. A change is live in the converter within about a minute.
 */
export function CorrectionsView({ system, botId }: { system: BotSystem; botId: string }) {
  const [data, setData] = useState<{ bot: Correction[]; global: Correction[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const gate = useRef(latestGate());

  const load = useCallback(async () => {
    const id = gate.current.begin();
    setError(null);
    try {
      const r = await hubClient.textCorrections({ system, botId });
      if (gate.current.isCurrent(id)) setData(r);
    } catch (e) {
      if (gate.current.isCurrent(id)) setError(errorText(e));
    }
  }, [system, botId]);

  useEffect(() => {
    setData(null);
    void load();
  }, [load]);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!data) return <p className="text-sm text-gray-500">Loading…</p>;
  return (
    <section className="space-y-4">
      <p className="text-sm text-gray-600">
        A correction tells the converter how to write a Latin word (or 2–3 words) in Cyrillic for this bot. Add one with{' '}
        <b>Correct</b> on the Conversions tab; changes are live within about a minute.
      </p>
      <RulesTable
        rules={data.bot}
        empty="No corrections for this bot yet."
        onUpdate={(r, body) => hubClient.textCorrectionUpdate({ system, botId }, r.id, body).then(async (saved) => (await load(), saved))}
        loadChanges={(r) => hubClient.textCorrectionChanges({ system, botId }, r.id)}
        canSuggest
      />
      {data.global.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-700">All-bots corrections that apply here (set by admins)</h3>
          <RulesTable rules={data.global} empty="" />
        </div>
      )}
      <BotCases system={system} botId={botId} />
    </section>
  );
}

/** This bot's checked conversions (*Looks right*), removable — a wrong one would block the right fix (P3). */
function BotCases({ system, botId }: { system: BotSystem; botId: string }) {
  const [cases, setCases] = useState<TextEvalCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setCases(await hubClient.textEvalCases({ system, botId }));
    } catch (e) {
      setError(errorText(e));
    }
  }, [system, botId]);
  useEffect(() => {
    setCases(null);
    void load();
  }, [load]);
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">Checked conversions for this bot</h3>
      <p className="text-xs text-gray-500">
        Saved with <b>Looks right</b>. Every correction save is tested against them; delete one that is not right.
      </p>
      {error ? (
        <p className="text-sm text-red-700">{error}</p>
      ) : !cases ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <CasesTable cases={cases} showBot={false} onDelete={(c) => hubClient.textEvalDelete({ system, botId }, c.id).then(load)} />
      )}
    </div>
  );
}

/** A table of rules. Without `onUpdate` it is read-only. A bot rule wins over an all-bots rule for the same word. */
export function RulesTable({
  rules,
  empty,
  onUpdate,
  loadChanges,
  canSuggest = false,
}: {
  rules: Correction[];
  empty: string;
  /** Resolves to the saved rule; its `evaluation` (P3) is shown under the row. */
  onUpdate?: (rule: Correction, body: RulePatch) => Promise<unknown>;
  loadChanges?: (rule: Correction) => Promise<CorrectionChange[]>;
  canSuggest?: boolean;
}) {
  const [editing, setEditing] = useState<{ id: string; cyrillic: string } | null>(null);
  const [history, setHistory] = useState<{ id: string; rows: CorrectionChange[] | null } | null>(null);
  const [error, setError] = useState<{ id: string; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ id: string; evaluation: CorrectionEvaluation | null } | null>(null);
  const [pending, setPending] = useState<{ id: string; body: RulePatch; message: string; evaluation: CorrectionEvaluation | null } | null>(null);

  if (rules.length === 0) return empty ? <p className="text-sm text-gray-500">{empty}</p> : null;

  const save = async (r: Correction, body: RulePatch) => {
    if (!onUpdate) return;
    setBusy(r.id);
    setError(null);
    setNotice(null);
    setPending(null);
    try {
      const saved = (await onUpdate(r, body)) as { evaluation?: CorrectionEvaluation | null } | undefined;
      setEditing(null);
      if (saved && 'evaluation' in saved) setNotice({ id: r.id, evaluation: saved.evaluation ?? null });
    } catch (e) {
      const ask = needsConfirm(messageOf(e));
      if (ask) setPending({ id: r.id, body, ...ask });
      else setError({ id: r.id, text: saveError(e) });
    } finally {
      setBusy(null);
    }
  };

  const toggleHistory = async (r: Correction) => {
    if (!loadChanges) return;
    if (history?.id === r.id) return setHistory(null);
    setHistory({ id: r.id, rows: null });
    try {
      const rows = await loadChanges(r);
      setHistory((h) => (h?.id === r.id ? { id: r.id, rows } : h));
    } catch (e) {
      setHistory(null);
      setError({ id: r.id, text: errorText(e) });
    }
  };

  const cols = 6;
  return (
    <table className="w-full text-sm bg-white border border-gray-200 rounded-lg">
      <thead className="text-left text-xs text-gray-500">
        <tr>
          <th className="p-2">Latin</th>
          <th className="p-2">Cyrillic</th>
          <th className="p-2">For</th>
          <th className="p-2">On</th>
          <th className="p-2">Changed</th>
          <th className="p-2" />
        </tr>
      </thead>
      <tbody>
        {rules.map((r) => (
          <Fragment key={r.id}>
            <tr className={`border-t ${r.enabled ? '' : 'text-gray-400'}`}>
              <td className="p-2 font-mono">{r.latin}</td>
              <td className="p-2 font-mono">
                {editing?.id === r.id ? (
                  <span className="flex gap-1">
                    <input
                      aria-label={`Cyrillic for ${r.latin}`}
                      className="border border-gray-300 rounded px-1 py-0.5 font-mono w-40"
                      value={editing.cyrillic}
                      maxLength={120}
                      onChange={(e) => setEditing({ id: r.id, cyrillic: e.target.value })}
                    />
                    <button type="button" className="text-blue-700" disabled={busy === r.id} onClick={() => void save(r, { cyrillic: editing.cyrillic })}>
                      Save
                    </button>
                    <button type="button" className="text-gray-500" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  r.cyrillic
                )}
              </td>
              <td className="p-2 text-gray-600">
                {ruleSource(r)}
                {r.suggestGlobal && <span className="ml-1 text-xs text-amber-700">(suggested for all bots)</span>}
              </td>
              <td className="p-2">
                {onUpdate ? (
                  <input
                    type="checkbox"
                    aria-label={`Correction ${r.latin} on`}
                    checked={r.enabled}
                    disabled={busy === r.id}
                    onChange={(e) => void save(r, { enabled: e.target.checked })}
                  />
                ) : r.enabled ? (
                  'Yes'
                ) : (
                  'No'
                )}
              </td>
              <td className="p-2 text-xs text-gray-500">
                {r.updatedBy ?? r.createdBy ?? '—'}, {fmtTime(r.updatedAt)}
              </td>
              <td className="p-2 text-right space-x-2 whitespace-nowrap">
                {onUpdate && editing?.id !== r.id && (
                  <button type="button" className="text-blue-700" onClick={() => setEditing({ id: r.id, cyrillic: r.cyrillic })}>
                    Edit
                  </button>
                )}
                {onUpdate && canSuggest && !r.suggestGlobal && r.enabled && (
                  <button type="button" className="text-blue-700" disabled={busy === r.id} onClick={() => void save(r, { suggestGlobal: true })}>
                    Suggest for all bots
                  </button>
                )}
                {loadChanges && (
                  <button type="button" className="text-gray-600" onClick={() => void toggleHistory(r)}>
                    History
                  </button>
                )}
              </td>
            </tr>
            {error?.id === r.id && (
              <tr>
                <td colSpan={cols} className="px-2 pb-2 text-sm text-red-700">
                  {error.text}
                </td>
              </tr>
            )}
            {notice?.id === r.id && (
              <tr>
                <td colSpan={cols} className="px-2 pb-2">
                  <EvaluationNote evaluation={notice.evaluation} />
                </td>
              </tr>
            )}
            {pending?.id === r.id && (
              <tr className="bg-amber-50">
                <td colSpan={cols} className="p-2 space-y-1">
                  <p className="text-sm text-amber-800">{pending.message}</p>
                  {pending.evaluation && <EvaluationNote evaluation={pending.evaluation} />}
                  <button
                    type="button"
                    className="px-2 py-1 text-sm rounded bg-amber-600 text-white"
                    disabled={busy === r.id}
                    onClick={() => void save(r, { ...pending.body, confirm: true })}
                  >
                    Save anyway
                  </button>{' '}
                  <button type="button" className="text-sm text-gray-600" onClick={() => setPending(null)}>
                    Cancel
                  </button>
                </td>
              </tr>
            )}
            {history?.id === r.id && (
              <tr className="bg-gray-50">
                <td colSpan={cols} className="p-2 text-xs">
                  {!history.rows ? (
                    'Loading…'
                  ) : (
                    <ul className="space-y-0.5">
                      {history.rows.map((c) => (
                        <li key={c.id}>
                          <span className="text-gray-500">{fmtTime(c.createdAt)}</span> {c.actor ?? '—'}: {changeText(c)}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

/** "Checked 12 conversions: 10 → 11 right" and the cases whose result changed (P3). */
export function EvaluationNote({ evaluation }: { evaluation: CorrectionEvaluation | null | undefined }) {
  if (evaluation === undefined) return null;
  return (
    <div className="text-xs text-gray-700 space-y-0.5">
      <p>{accuracyText(evaluation)}</p>
      {evaluation?.changed.slice(0, 10).map((c) => (
        <p key={c.caseId} className="font-mono">
          {c.latin}: {c.before} → {c.after} <span className={c.after.trim().toLowerCase() === c.expected.trim().toLowerCase() ? 'text-green-700' : 'text-red-700'}>(expected {c.expected})</span>
        </p>
      ))}
    </div>
  );
}

function changeText(c: CorrectionChange): string {
  if (!c.before) return `created ${c.after.latin} → ${c.after.cyrillic}`;
  const parts: string[] = [];
  if (c.before.cyrillic !== c.after.cyrillic) parts.push(`${c.before.cyrillic} → ${c.after.cyrillic}`);
  if (c.before.enabled !== c.after.enabled) parts.push(c.after.enabled ? 'switched on' : 'switched off');
  if (c.before.suggestGlobal !== c.after.suggestGlobal) parts.push(c.after.suggestGlobal ? 'suggested for all bots' : 'suggestion closed');
  if (c.before.note !== c.after.note) parts.push('note changed');
  return parts.join(', ') || 'saved';
}

/** The inline form behind Conversions › Correct: prefilled with the row; saves a bot correction. */
export function CorrectForm({
  system,
  botId,
  latin,
  cyrillic,
  onDone,
}: {
  system: BotSystem;
  botId: string;
  latin: string;
  cyrillic: string;
  onDone: (evaluation: CorrectionEvaluation | null | undefined) => void;
}) {
  const [form, setForm] = useState({ latin, cyrillic, note: '', suggestGlobal: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await hubClient.textCorrectionCreate(
        { system, botId },
        { latin: form.latin, cyrillic: form.cyrillic, note: form.note || undefined, suggestGlobal: form.suggestGlobal },
      );
      onDone(saved.evaluation);
    } catch (e) {
      setError(saveError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <input
        aria-label="Latin"
        className="border border-gray-300 rounded px-2 py-1 font-mono w-36"
        value={form.latin}
        maxLength={80}
        onChange={(e) => setForm({ ...form, latin: e.target.value })}
      />
      →
      <input
        aria-label="Cyrillic"
        className="border border-gray-300 rounded px-2 py-1 font-mono w-40"
        value={form.cyrillic}
        maxLength={120}
        onChange={(e) => setForm({ ...form, cyrillic: e.target.value })}
      />
      <input
        aria-label="Note"
        className="border border-gray-300 rounded px-2 py-1 w-48"
        placeholder="Note (optional)"
        value={form.note}
        maxLength={200}
        onChange={(e) => setForm({ ...form, note: e.target.value })}
      />
      <label className="flex items-center gap-1 text-gray-600">
        <input type="checkbox" checked={form.suggestGlobal} onChange={(e) => setForm({ ...form, suggestGlobal: e.target.checked })} />
        Suggest for all bots
      </label>
      <button
        type="button"
        disabled={busy || !form.latin.trim() || !form.cyrillic.trim()}
        onClick={() => void submit()}
        className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save correction'}
      </button>
      {error && <span className="text-red-700">{error}</span>}
    </div>
  );
}

/** Every case, removable: a wrong *Looks right* would otherwise block the right all-bots fix forever (final review #3). */
export function CasesTable({ cases, onDelete, showBot }: { cases: TextEvalCase[]; onDelete: (c: TextEvalCase) => Promise<void>; showBot: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!cases.length) return <p className="text-sm text-gray-500">No checked conversions yet.</p>;
  return (
    <div className="space-y-1">
      {error && <p className="text-sm text-red-700">{error}</p>}
      <table className="w-full text-sm bg-white border border-gray-200 rounded-lg">
        <thead className="text-left text-xs text-gray-500">
          <tr>
            <th className="p-2">Customer wrote</th>
            <th className="p-2">Right conversion</th>
            {showBot && <th className="p-2">For</th>}
            <th className="p-2">From</th>
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {cases.map((c) => (
            <tr key={c.id} className="border-t">
              <td className="p-2 font-mono">{c.latin}</td>
              <td className="p-2 font-mono">{c.expected}</td>
              {showBot && <td className="p-2 text-gray-600">{c.scope === 'global' ? 'All bots' : `${c.system} / ${c.botId}`}</td>}
              <td className="p-2 text-gray-600">{SOURCE_TEXT[c.source] ?? c.source}</td>
              <td className="p-2 text-right">
                <button
                  type="button"
                  className="text-red-700"
                  disabled={busy === c.id}
                  onClick={() => {
                    setBusy(c.id);
                    setError(null);
                    onDelete(c)
                      .catch((e) => setError(errorText(e)))
                      .finally(() => setBusy(null));
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SOURCE_TEXT: Record<string, string> = { confirmed: 'Looks right', seed: 'Starter', correction: 'Correction' };
